import type React from "react";
import { useEffect, useMemo } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import type { PollDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { useTranslation } from "@/translations";

export type PollData = Pick<PollDto, "question" | "allowMultipleAnswers"> & {
  options: string[];
  durationInDays: number;
};

type PollFormValues = Omit<PollData, "options"> & {
  options: { value: string }[];
};

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 7;
const DURATION_OPTIONS = [7, 14];

type EditPollModalProps = ModalBaseProps & {
  poll?: PollData;
  // ONLY allow editing the question and options
  limitedEditing?: boolean;
  onConfirm: (data: PollData) => void;
};

export function EditPollModal({
  isOpened,
  onOpenChange,
  poll,
  onConfirm,
  limitedEditing,
}: EditPollModalProps): React.ReactNode {
  const { t } = useTranslation();

  const defaultFormValues: PollFormValues = useMemo(
    () => ({
      question: poll?.question ?? "",
      options: poll ? poll.options.map((o) => ({ value: o })) : [{ value: "" }, { value: "" }],
      durationInDays: poll?.durationInDays ?? 7,
      allowMultipleAnswers: poll?.allowMultipleAnswers ?? false,
    }),
    [poll],
  );
  const form = useForm<PollFormValues>({
    defaultValues: defaultFormValues,
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "options",
  });
  const isEditMode = Boolean(poll);

  useEffect(() => {
    if (isOpened) {
      form.reset(defaultFormValues);
    }
  }, [defaultFormValues, form, isOpened]);

  // Programatically trigger form validation instead of using `Button<submit>` to prevent parent form submission
  const handleConfirm = () => {
    void form.handleSubmit((values) => {
      onConfirm({
        question: values.question,
        options: values.options.map((o) => o.value),
        durationInDays: values.durationInDays,
        allowMultipleAnswers: values.allowMultipleAnswers,
      });
      onOpenChange(false);
    })();
  };

  return (
    <Modal.Root
      title={t("page.admin-create-post.edit-poll-modal.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      size="sm"
      data-testid="edit-poll-modal"
      isNested
    >
      <FormProvider {...form}>
        <Modal.Body>
          <Stack gap="md">
            <FormField label={t("component.community-feed.create-or-edit-modal.form.question.label")} required>
              <FormInput<PollFormValues>
                name="question"
                placeholder={t("component.community-feed.create-or-edit-modal.form.question.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.admin-create-post.create-poll-modal.question.label"),
                  },
                }}
              />
            </FormField>

            <FormField label={t("component.community-feed.create-or-edit-modal.form.options.label")} required>
              <Stack gap="sm">
                {fields.map((field, index) => (
                  <Inline key={field.id} align="start" gap="sm" wrap={false}>
                    <div className="flex-1">
                      <FormInput<PollFormValues>
                        name={`options.${index}.value`}
                        placeholder={t("component.community-feed.create-or-edit-modal.form.options.placeholder", {
                          number: index + 1,
                        })}
                        data-testid="option-input"
                        rules={{
                          validate: {
                            required: createRequiredStringRule(
                              t,
                              "component.community-feed.create-or-edit-modal.form.options.label",
                            ),
                          },
                        }}
                      />
                    </div>
                    <IconButton
                      title={t("common.action.delete")}
                      icon={<Trash01Icon />}
                      variant="filled"
                      tone="error"
                      disabled={fields.length <= MIN_OPTIONS}
                      onClick={() => remove(index)}
                    />
                  </Inline>
                ))}
                {!limitedEditing && (
                  <Button
                    variant="ghostPrimary"
                    icon={<PlusIcon />}
                    disabled={fields.length >= MAX_OPTIONS}
                    onClick={() => append({ value: "" })}
                    data-testid="add-option-btn"
                  >
                    {t("component.community-feed.create-or-edit-modal.form.btn.add-option")}
                  </Button>
                )}
              </Stack>
            </FormField>
            {!limitedEditing && (
              <>
                <FormField label={t("component.community-feed.create-or-edit-modal.form.duration.label")} required>
                  <FormSelect<PollFormValues, number>
                    name="durationInDays"
                    items={DURATION_OPTIONS}
                    keySelector={(x) => x}
                    renderOption={(x) =>
                      t("component.community-feed.create-or-edit-modal.form.duration.days", { count: x })
                    }
                    usePortal
                  />
                </FormField>
                <FormCheckbox<PollFormValues>
                  name="allowMultipleAnswers"
                  data-testid="allow-multiple-answers-checkbox"
                  label={t("component.community-feed.create-or-edit-modal.form.allow-multiple-answers.label")}
                />
              </>
            )}
          </Stack>
        </Modal.Body>
        <Modal.Actions>
          <Button onClick={handleConfirm} data-testid="confirm-btn">
            {isEditMode ? t("common.action.save") : t("common.action.add")}
          </Button>
        </Modal.Actions>
      </FormProvider>
    </Modal.Root>
  );
}
